'use client'

import { type DragEvent, useCallback, useRef, useState } from 'react'

type UseFileDropZoneInput = {
  onFiles: (files: FileList) => void
  disabled?: boolean
}

/**
 * Drag-and-drop file handling for a whole surface (the composer panel, the assistant bar). Tracks nested
 * enter/leave pairs so the overlay does not flicker while the pointer crosses child elements.
 */
export default function useFileDropZone({ onFiles, disabled }: UseFileDropZoneInput) {
  const [isDragging, setIsDragging] = useState(false)
  const depthRef = useRef(0)

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !hasFiles(event)) return
      event.preventDefault()
      depthRef.current += 1
      setIsDragging(true)
    },
    [disabled]
  )

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !hasFiles(event)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [disabled]
  )

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFiles(event)) return
    depthRef.current = Math.max(0, depthRef.current - 1)
    if (depthRef.current === 0) setIsDragging(false)
  }, [])

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      depthRef.current = 0
      setIsDragging(false)
      if (disabled) return
      if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files)
    },
    [disabled, onFiles]
  )

  return { isDragging, dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop } }
}

function hasFiles(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}
